// Das eigene Werk je Branche. Die Mitarbeitendenzahl zeigen Figuren auf dem Hof (people.js).
import * as THREE from "three"
import { COLORS } from "./palette.js"
import { at, group, hall, chimney, silo, box, cylinder, truck, crate, container, towerCrane, excavator, heap, car, tree, roundTree } from "./builders.js"

function hooks(g) {
  g.userData.smoke ??= []
  g.userData.roofs ??= []
  g.userData.parking ??= []
  g.userData.halls ??= []
  return g.userData
}

function addHall(g, options, x, z, rotation = 0) {
  const h = at(hall(options), x, 0, z, rotation)
  g.add(h)
  const data = hooks(g)
  data.halls.push(h)
  data.roofs.push({ object: h, ...h.userData.roof })
  return h
}

function addChimney(g, x, z, height = 6) {
  const c = at(chimney(height), x, 0, z)
  g.add(c)
  hooks(g).smoke.push(new THREE.Vector3(x, c.userData.smokeTop.y, z))
  return c
}

function addBoiler(g, x, z) {
  const boiler = at(box(1.4, 1.3, 1.1, COLORS.orange), x, 0, z)
  boiler.add(at(cylinder(0.15, 0.15, 0.8, COLORS.steelDark, 6), 0.3, 0.65, 0))
  g.add(boiler)
  hooks(g).boiler = boiler
}

function addFleet(g, positions) {
  const data = hooks(g)
  data.fleet = positions.map(([x, z, r]) => {
    const t = at(truck(COLORS.white, COLORS.green), x, 0, z, r)
    g.add(t)
    return t
  })
}

const BUILDERS = {
  maschinenbau(g) {
    addHall(g, { w: 11, d: 7, h: 3.4, sawtooth: 5 }, -1.5, 0)
    addHall(g, { w: 6, d: 5, h: 2.8 }, 7.2, -1)
    g.add(at(box(3.5, 4.2, 3, COLORS.wall), 7.5, 0, 4.5))
    g.add(at(box(3.6, 0.2, 3.1, COLORS.roof), 7.5, 4.2, 4.5))
    addChimney(g, -7.8, -2.5, 6.5)
    addBoiler(g, -8, 1.5)
    for (let i = 0; i < 4; i++) g.add(at(crate(COLORS.wallWarm), -4 + i * 1.1, 0, 5.2))
    addFleet(g, [[1, 7, 0], [4.5, 7.5, 0]])
  },
  kunststoff(g) {
    addHall(g, { w: 10, d: 7, h: 3.6 }, 0, 0)
    for (let i = 0; i < 3; i++) g.add(at(silo(6, 0.9, COLORS.wall), -8 + i * 2.1, 0, -1.5))
    g.add(at(box(6.5, 0.25, 0.25, COLORS.steel), -6, 5, -1.5))
    addChimney(g, 6.8, -2.5, 7)
    addBoiler(g, 7, 1.5)
    addFleet(g, [[-2, 6.5, 0], [2, 7, 0]])
  },
  lebensmittel(g) {
    addHall(g, { w: 10, d: 7, h: 3.2, wall: COLORS.white }, 0, 0)
    for (let i = 0; i < 2; i++) {
      const tank = at(cylinder(1, 1, 3.6, COLORS.wall, 12), -7.2, 0, -2.4 + i * 2.6)
      tank.rotation.z = Math.PI / 2
      tank.position.y = 1.1
      g.add(tank)
    }
    g.add(at(box(2.4, 1.2, 1.6, COLORS.steel), 6.8, 0, -2))
    addChimney(g, 6.8, 1.2, 5.5)
    addBoiler(g, 4.5, -5)
    addFleet(g, [[-1, 6.5, 0], [3, 7, 0]])
  },
  bau(g) {
    addHall(g, { w: 8, d: 5, h: 2.6, roof: COLORS.roofLight }, -2, -2)
    g.add(at(box(3, 2.4, 2.2, COLORS.wallWarm), 5.2, 0, -3))
    g.userData.crane = at(towerCrane(10), 3, 0, 3)
    g.userData.excavator = at(excavator(), -5, 0, 4.5, 0.4)
    g.add(g.userData.crane, g.userData.excavator)
    g.add(at(heap(1.6, COLORS.gravel), -1, 0, 5))
    g.add(at(heap(1.2, COLORS.soil), 1.8, 0, 6))
    addChimney(g, -6.8, -3.5, 4.5)
    addBoiler(g, -6.8, -0.5)
    addFleet(g, [[6, 4, 0], [6, 6.8, 0]])
  },
  handel(g) {
    addHall(g, { w: 15, d: 8, h: 4 }, 0, -0.5)
    for (let i = 0; i < 5; i++) g.add(at(box(1.4, 1.6, 0.25, COLORS.steelDark), -6 + i * 3, 0, 3.6))
    g.add(at(container(COLORS.green), -8.5, 0, 6))
    g.add(at(container(COLORS.rust), -8.5, 1, 6))
    addChimney(g, 8.4, -3.5, 4.5)
    addBoiler(g, 8.6, -0.5)
    addFleet(g, [[-3, 6.5, 0], [0.6, 6.5, 0], [4.2, 6.5, 0]])
  },
  dienstleistung(g) {
    const tower = at(box(6, 7.5, 5, COLORS.wall), 0, 0, -1)
    g.add(tower)
    for (let floor = 0; floor < 5; floor++) {
      g.add(at(box(6.06, 0.55, 5.06, COLORS.glass), 0, 1.1 + floor * 1.3, -1))
    }
    g.add(at(box(6.2, 0.25, 5.2, COLORS.roof), 0, 7.5, -1))
    hooks(g).roofs.push({ object: tower, w: 6, d: 5, y: 7.75 })
    addChimney(g, 4.6, -3, 3)
    addBoiler(g, 4.8, 0)
    for (let i = 0; i < 4; i++) g.add(at(car([COLORS.red, COLORS.slate, COLORS.white, COLORS.navy600][i]), -4 + i * 1.6, 0, 5, Math.PI / 2))
    addFleet(g, [[4.5, 5.5, 0]])
    g.add(at(roundTree(1.2), -4.5, 0, -1))
  }
}

export function buildFactory(industry) {
  const g = new THREE.Group()
  hooks(g)
  BUILDERS[industry](g)
  // Parkplatz/Hof
  const yard = box(20, 0.06, 15, COLORS.concrete)
  yard.receiveShadow = true
  yard.castShadow = false
  yard.position.y = 0.01
  yard.userData.yard = true
  g.add(yard)
  for (const [x, z] of [[-9.5, -6.5], [9.5, -6.5], [-9.5, 6.8]]) g.add(at(tree(1.1), x, 0, z))
  return g
}
